import { createGlobalStyle } from 'styled-components';
import { BREAKPOINTS } from "../../../app/appConstants";

/**
 * Style Guide https://www.figma.com/file/O3ikmxQ658tKqvq3irCUqv/Jetvix-UI-kit?type=design&node-id=174-935&t=K7Bn4woVNQ9b673w-4
 */
export const GlobalButtonsStyled = createGlobalStyle<{ isPlatformMobile: boolean; }>`

    .btn {
        font-size: 16px;
        font-weight: 600;

        border-radius: 16px;
        padding: 12px;
        border: none;

        @media (max-width: ${BREAKPOINTS.extraSmall}px) {
            padding: 8px;
            gap: 4px;
        }

        display: inline-flex;
        justify-content: center;
        align-items: center;
        gap: 6px;
        white-space: nowrap;

        &.color-white {
            color: var(--color-label-primary);
        }

        &:hover {
            transition: color 0.3s ease-out, background 0.3s ease-out;
        }

        svg {
            width: 16px;
            height: 16px;
        }
    }

    .btn-group-xs > .btn,
    .btn-xs {
        font-weight: 600;
        font-size: 16px;

        border-radius: 12px;
        padding: 4px 12px;
    }

    .btn-group-sm > .btn,
    .btn-sm {
        font-weight: 600;
        font-size: 16px;
        border-radius: 16px;
        padding: 8px 12px;
    }
    
    .btn-m14 {
        font-weight: 600;
        font-size: 14px;
        border-radius: 16px;
        padding: 12px;
    }

    .btn-group-lg > .btn,
    .btn-lg {
        font-size: 16px;
        font-weight: 600;
        border-radius: 16px;
        padding: 16px 24px;
    }

    .square {
        padding: 0;
        min-width: 48px;
        min-height: 48px;
        max-width: 48px;
        max-height: 48px;

        &.btn-lg {
            padding: 0;
            min-width: 56px;
            min-height: 56px;
            max-width: 56px;
            max-height: 56px;
        }

        &.btn-sm {
            padding: 0;
            min-width: 40px;
            min-height: 40px;
            max-width: 40px;
            max-height: 40px;
        }

        &.btn-xs {
            padding: 0;
            min-width: 32px;
            min-height: 32px;
            max-width: 32px;
            max-height: 32px;
        }

        svg {
            width: unset;
            height: unset;
        }
    }

    // Primary
    .btn-primary {
        background: var(--color-button-primary-default);
        color: var(--color-label-neutral-primary-black);

        &:disabled {
            background: var(--color-button-primary-inactive);
            color: var(--color-label-neutral-primary-black);
        }

        &:focus {
            box-shadow: 0 0 0 0.25rem rgb(49 132 253 / 50%);
        }

        &:focus,
        &:hover,
        &:active {
            background: var(--color-button-primary-highlight);
            color: var(--color-label-neutral-primary-black);
        }

        &:focus {
            background: ${({ isPlatformMobile }) => isPlatformMobile ? 'var(--color-button-primary-default)' : 'var(--color-button-primary-highlight)'};
        }
    }

    .btn-link {
        color: var(--color-button-primary-default);
        text-decoration: none;

        &:hover,
        &:focus {
            color: var(--color-label-primary);
            text-decoration: underline;
        }
    }

    // Secondary
    .btn-secondary {
        &,
        &.active,
        &:active,
        &:disabled,
        &:focus,
        &:hover {
            color: var(--color-label-primary);
            background: var(--color-button-secondary-default);
        }

        &.active, &:hover, &:active, &:focus {
            background: var(--color-button-secondary-highlight);
            text-decoration: none;

            &.negative {
                color: var(--color-label-negative-transparent);
            }
        }

        &:focus {
            background: ${({ isPlatformMobile }) => isPlatformMobile ? 'var(--color-button-secondary-default)' : 'var(--color-button-secondary-highlight)'};
        }

        &:disabled {
            color: var(--color-label-secondary);
            background: var(--color-button-secondary-inactive);
        }

        &.negative {
            color: var(--color-label-negative);
        }
    }

    // Tertiary
    .btn-tertiary {
        &,
        &.active,
        &:active,
        &:disabled,
        &:focus,
        &:hover {
            color: var(--color-label-primary);
            background: transparent;
            border-color: transparent;
        }

        &:hover, &:active, &:focus {
            color: var(--color-label-secondary);

            &.negative {
                color: var(--color-label-negative-transparent);
            }
        }

        &:disabled {
            color: var(--color-label-tertiary);
        }

        &.negative {
            color: var(--color-label-negative);
        }
    }

    // Preset
    .btn-preset {
        background: var(--color-button-secondary-default);
        color: var(--color-label-primary);
        padding: 8px;
        min-width: 48px;
        flex-shrink: 0;

        &:active {
            background: var(--color-button-secondary-inactive);
            color: var(--color-label-secondary);
        }

        &:hover, &.active {
            background: var(--color-button-secondary-highlight);
            color: var(--color-label-primary);
        }

        &:disabled {
            color: rgba(255, 255, 255, 0.4);
        }
    }

    // @deprecated
    .btn-outline-primary {
        border: 2px solid var(--color-yellow);
        box-sizing: border-box;
        border-radius: 2px;
        color: var(--color-yellow);
        background: transparent;

        &:hover {
            color: var(--color-yellow-light);
            background: transparent;
            border: 2px solid var(--color-yellow-light);
        }

        &.active,
        &:active {
            color: var(--color-yellow);
            background: transparent;
            border: 2px solid var(--color-yellow);
        }
    }

    .btn-outline-danger {
        color: var(--color-negative);
        background: transparent;
        border-color: transparent;

        &:hover {
            color: var(--color-negative-lite);
            background: transparent;
            border-color: transparent;
        }

        &.active,
        &:active {
            color: var(--color-negative-lite);
            background: transparent;
            border-color: transparent;
        }
    }

    .btn-outline-info {
        &,
        &:disabled,
        &:focus,
        &:hover {
            color: #fff;
            background: transparent;
            border-color: transparent;
        }

        &:hover {
            color: #fff;
            background: var(--color-purple);
            border-color: var(--color-purple);
            text-decoration: none;
        }

        &.active,
        &:active {
            color: #64A7FF;
            background: var(--color-purple);
            border-color: var(--color-purple);
        }

        &:disabled {
            color: var(--color-label-secondary);
        }

        &.negative {
            color: var(--color-label-negative);
        }
    }

    .btn-info {
        color: var(--color-label-primary);
        background: var(--color-purple);
        border-radius: 16px;

        &:focus {
            color: var(--color-white);
            background: var(--color-purple);
            box-shadow: 0 0 0 0.25rem rgb(49 132 253 / 50%);
        }

        &:hover {
            color: var(--color-white);
            background: var(--color-purple-highlight);
        }

        &:active, &.active {
            color: var(--color-white);
            background: var(--color-purple-highlight);
        }

        &:disabled {
            color: var(--color-label-secondary);
            background: #30334F;
        }
    }

    .btn-outline-warning {
        &,
        &:disabled,
        &:focus,
        &:hover,
        &:active {
            color: var(--color-yellow);
            background: transparent;
            border-color: transparent;
        }

        svg {
            transition: fill .15s ease-in-out;
            fill: var(--color-yellow);
        }

        &:focus {
            box-shadow: 0 0 0 0.25rem rgb(255 221 60 / 50%);
        }

        &:disabled {
            opacity: 0.5;
        }
    }

    // App
    .app {

        .btn {
            &:focus {
                box-shadow: none !important;
            }

            &.color-white {
                &:hover {
                    color: var(--color-label-primary);
                }
            }
        }

        .btn-primary {
            &:hover {
                background: var(--color-button-primary-highlight);
            }
        }

        .btn-tertiary {
            &:active,
            &:focus,
            &:hover {
                color: var(--color-label-primary);
            }
        }

        .btn-outline-primary {
            &:hover {
                color: var(--color-yellow);
                background: transparent;
                border: 2px solid var(--color-yellow);
            }
        }

        .btn-outline-danger {
            &:hover {
                color: var(--color-negative);
                background: transparent;
                border-color: transparent;
            }
        }

        .btn-preset {
            &:hover:not(.active) {
                background: var(--color-button-secondary-default);
                color: var(--color-label-primary);
            }
        }

        .btn-info {
            &:hover {
                color: var(--color-white);
                background: var(--color-purple);
                border: 1px solid var(--color-purple);
            }

            &:active, &.active {
                color: var(--color-white);
                background: var(--color-purple-highlight);
                border: 1px solid var(--color-purple-highlight);
            }
        }

        .btn-link {
            &:hover {
                color: var(--color-button-primary-default);
                text-decoration: underline;
            }
        }

        .btn-tertiary {
            &:focus,
            &:hover {
                color: var(--color-label-primary);
            }
        }
    }
`;